import { clipChroma, compositeAlpha, contrastRatioValidator, decompositeAlpha, isColorDisplayable, toRgb, toHcl } from "../lib/color";
import { wcagContrastRatio } from "../lib/color/srgb";
import { cloneTheme, defaultPressableTheme, pressableColors } from "./PressableThemeProvider";

// Generate a keyline for a provided color to match the appearance of a default
// keyline over a surface color.
const getKeylineColor = (color, defaultKeylineColor, surfaceColor, opacity) => {
  // Determine what color results when the default keyline is rendered above the
  // surface.
  const compositedDefault = toHcl(compositeAlpha(defaultKeylineColor, surfaceColor)); // Generate the color that our keyline should look like when rendered above
  // the surface color:
  //
  // Set our keyline color to match the input color, but don’t let the lightness
  // exceed the composite color. This will create a keyline that blends into the
  // input color when possible, but always has at least the same amount of
  // contrast as the default keyline (creating a border around colors that blend
  // into the surface).

  const compositedKeyline = toHcl(color);
  compositedKeyline.l = Math.min(compositedKeyline.l, compositedDefault.l); // We then want generate a new color with the provided opacity that matches
  // the composited keyline when rendered above the surface color. This is our
  // keyline.
  //
  // Both of these transforms could result in colors that might not exist, so we
  // decrease the chroma until we find colors that satisfy our constraints.

  const decompositeKeyline = c => decompositeAlpha(opacity, c, surfaceColor);

  return decompositeKeyline(clipChroma(compositedKeyline, color => {
    return isColorDisplayable(decompositeKeyline(color));
  }));
};

export const createPressableTheme = colorStr => {
  if (!colorStr) {
    return defaultPressableTheme;
  }

  const isContrastValid = contrastRatioValidator({
    fontSize: 14,
    weight: 'medium'
  });
  const color = toRgb(colorStr);
  const white = '#ffffff';
  const black = '#000000';
  const passOverWhite = isContrastValid(color, white);
  const passOverBlack = isContrastValid(color, black);

  if (!passOverWhite && !passOverBlack) {
    return defaultPressableTheme;
  }

  const theme = cloneTheme(defaultPressableTheme);
  theme.fill.primary = color;
  let primaryText = toHcl(color);

  while (!isContrastValid(white, primaryText)) {
    primaryText.l -= 5;

    if (primaryText.l <= 0) {
      primaryText = toHcl(black);
    }
  }

  theme.color.primary = primaryText;
  theme.keyline.primary = getKeylineColor(color, theme.keyline.base, pressableColors.gray100, 0.8);

  if (passOverWhite) {
    theme.color.onPrimary = white;
  } else if (passOverBlack) {
    let textOnPrimary = toHcl(color); // This contrast ratio is based on the default contrast in our buttons.

    while (wcagContrastRatio(color, textOnPrimary) <= 10) {
      textOnPrimary.l -= 5;

      if (textOnPrimary.l <= 0) {
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'HCL'.
        textOnPrimary = black;
        break;
      }
    }

    theme.color.onPrimary = textOnPrimary;
  }

  return theme;
};